// databaseFormHelpers — form-state shape + per-engine builders for the
// shared DatabaseEditFormBody (Slice G1).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice G1.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type {
  DatabaseChange,
  DatabaseDef,
  MssqlContainment,
  MssqlRecoveryModel,
} from '../../../../sql-resolver/types/ddlEdit';
import type { ParsedParkedDatabase } from '../../inline/parseParkedDatabaseStatement';

export type DatabaseEditMode = 'create' | 'alter';

export interface DatabaseFormValues {
  readonly name: string;
  readonly quoted: boolean;
  readonly mysqlSchema: boolean;
  readonly ifNotExists: boolean;
  // mssql create
  readonly mssqlContainment: '' | MssqlContainment;
  readonly mssqlCollate: string;
  // pgsql create
  readonly pgsqlOwner: string;
  readonly pgsqlTemplate: string;
  readonly pgsqlEncoding: string;
  readonly pgsqlLcCollate: string;
  readonly pgsqlLcCtype: string;
  readonly pgsqlTablespace: string;
  readonly pgsqlConnectionLimit: string;
  // mysql create
  readonly mysqlCharacterSet: string;
  readonly mysqlCollate: string;
  // oracle create
  readonly oracleAdminUser: string;
  readonly oracleAdminPassword: string;
  readonly oracleFileNameConvertText: string;
  // alter — mssql
  readonly mssqlNewName: string;
  readonly mssqlRecovery: '' | MssqlRecoveryModel;
  readonly mssqlCompatibilityLevel: string;
  // alter — pgsql
  readonly pgsqlRenameTo: string;
  readonly pgsqlOwnerTo: string;
  // alter — oracle
  readonly oraclePdbOpen: '' | 'open' | 'close';
}

export const DEFAULT_DATABASE_FORM_VALUES: DatabaseFormValues = {
  name: '',
  quoted: false,
  mysqlSchema: false,
  ifNotExists: false,
  mssqlContainment: '',
  mssqlCollate: '',
  pgsqlOwner: '',
  pgsqlTemplate: '',
  pgsqlEncoding: '',
  pgsqlLcCollate: '',
  pgsqlLcCtype: '',
  pgsqlTablespace: '',
  pgsqlConnectionLimit: '',
  mysqlCharacterSet: '',
  mysqlCollate: '',
  oracleAdminUser: '',
  oracleAdminPassword: '',
  oracleFileNameConvertText: '',
  mssqlNewName: '',
  mssqlRecovery: '',
  mssqlCompatibilityLevel: '',
  pgsqlRenameTo: '',
  pgsqlOwnerTo: '',
  oraclePdbOpen: '',
};

export function valuesFromParsed(parsed: ParsedParkedDatabase): Partial<DatabaseFormValues> {
  return {
    name: parsed.name,
    quoted: parsed.quoted,
    mysqlSchema: parsed.mysqlSchema,
    ifNotExists: parsed.ifNotExists,
  };
}

function trimOrUndef(raw: string): string | undefined {
  const t = raw.trim();
  return t.length === 0 ? undefined : t;
}

type MutableDatabaseDef = {
  -readonly [K in keyof DatabaseDef]: DatabaseDef[K];
};

function buildMssqlCreate(v: DatabaseFormValues): Partial<MutableDatabaseDef> {
  const out: Partial<MutableDatabaseDef> = {};
  if (v.mssqlContainment !== '') out.mssqlContainment = v.mssqlContainment;
  const collate = trimOrUndef(v.mssqlCollate);
  if (collate !== undefined) out.mssqlCollate = collate;
  return out;
}

function buildPgsqlCreate(v: DatabaseFormValues): Partial<MutableDatabaseDef> {
  const out: Partial<MutableDatabaseDef> = {};
  const owner = trimOrUndef(v.pgsqlOwner);
  if (owner !== undefined) out.pgsqlOwner = owner;
  const template = trimOrUndef(v.pgsqlTemplate);
  if (template !== undefined) out.pgsqlTemplate = template;
  const encoding = trimOrUndef(v.pgsqlEncoding);
  if (encoding !== undefined) out.pgsqlEncoding = encoding;
  const lcCollate = trimOrUndef(v.pgsqlLcCollate);
  if (lcCollate !== undefined) out.pgsqlLcCollate = lcCollate;
  const lcCtype = trimOrUndef(v.pgsqlLcCtype);
  if (lcCtype !== undefined) out.pgsqlLcCtype = lcCtype;
  const tablespace = trimOrUndef(v.pgsqlTablespace);
  if (tablespace !== undefined) out.pgsqlTablespace = tablespace;
  if (v.pgsqlConnectionLimit.trim().length > 0) {
    const n = Number.parseInt(v.pgsqlConnectionLimit.trim(), 10);
    if (Number.isFinite(n)) out.pgsqlConnectionLimit = n;
  }
  return out;
}

function buildMysqlCreate(v: DatabaseFormValues): Partial<MutableDatabaseDef> {
  const out: Partial<MutableDatabaseDef> = {};
  if (v.mysqlSchema) out.mysqlSchema = true;
  const charset = trimOrUndef(v.mysqlCharacterSet);
  if (charset !== undefined) out.mysqlCharacterSet = charset;
  const collate = trimOrUndef(v.mysqlCollate);
  if (collate !== undefined) out.mysqlCollate = collate;
  return out;
}

function buildOracleCreate(v: DatabaseFormValues): Partial<MutableDatabaseDef> {
  const out: Partial<MutableDatabaseDef> = {};
  const user = trimOrUndef(v.oracleAdminUser);
  if (user !== undefined) out.oracleAdminUser = user;
  const password = trimOrUndef(v.oracleAdminPassword);
  if (password !== undefined) out.oracleAdminPassword = password;
  const fileText = trimOrUndef(v.oracleFileNameConvertText);
  if (fileText !== undefined) out.oracleFileNameConvertText = fileText;
  return out;
}

export function buildDatabaseDefFromForm(
  engine: SqlEngine,
  v: DatabaseFormValues,
): DatabaseDef {
  const base: DatabaseDef = {
    name: v.name.trim(),
    ...(v.quoted ? { quoted: true } : {}),
    // Only MySQL's CREATE DATABASE grammar accepts IF NOT EXISTS. PostgreSQL's
    // CREATE DATABASE has no such clause (CREATE SCHEMA IF NOT EXISTS is the PG
    // analogue, handled by the schema form), so we never set it for postgres. (F08)
    ...(v.ifNotExists && engine === 'mysql' ? { ifNotExists: true } : {}),
  };
  if (engine === 'mssql') return { ...base, ...buildMssqlCreate(v) };
  if (engine === 'postgres') return { ...base, ...buildPgsqlCreate(v) };
  if (engine === 'mysql') return { ...base, ...buildMysqlCreate(v) };
  if (engine === 'oracle') return { ...base, ...buildOracleCreate(v) };
  return base;
}

type MutableDatabaseChange = {
  -readonly [K in keyof DatabaseChange]: DatabaseChange[K];
};

export function buildDatabaseChangeFromForm(
  engine: SqlEngine,
  v: DatabaseFormValues,
): DatabaseChange {
  if (engine === 'mssql') {
    const c: MutableDatabaseChange = {};
    const newName = trimOrUndef(v.mssqlNewName);
    if (newName !== undefined) c.mssqlNewName = newName;
    if (v.mssqlRecovery !== '') c.mssqlRecovery = v.mssqlRecovery as MssqlRecoveryModel;
    if (v.mssqlCompatibilityLevel.trim().length > 0) {
      const n = Number.parseInt(v.mssqlCompatibilityLevel.trim(), 10);
      if (Number.isFinite(n)) c.mssqlCompatibilityLevel = n;
    }
    return c;
  }
  if (engine === 'postgres') {
    const c: MutableDatabaseChange = {};
    const renameTo = trimOrUndef(v.pgsqlRenameTo);
    if (renameTo !== undefined) c.pgsqlRenameTo = renameTo;
    const ownerTo = trimOrUndef(v.pgsqlOwnerTo);
    if (ownerTo !== undefined) c.pgsqlOwnerTo = ownerTo;
    return c;
  }
  if (engine === 'mysql') {
    const c: MutableDatabaseChange = {};
    const charset = trimOrUndef(v.mysqlCharacterSet);
    if (charset !== undefined) c.mysqlCharacterSet = charset;
    const collate = trimOrUndef(v.mysqlCollate);
    if (collate !== undefined) c.mysqlCollate = collate;
    return c;
  }
  if (engine === 'oracle') {
    const c: MutableDatabaseChange = {};
    if (v.oraclePdbOpen === 'open') c.oraclePdbOpen = true;
    if (v.oraclePdbOpen === 'close') c.oraclePdbOpen = false;
    return c;
  }
  return {};
}
